import { useCallback, useEffect, useRef } from "react";
import { Box, Divider, IconButton, MenuItem, TextField, Tooltip } from "@mui/material";
import FormatBoldIcon from "@mui/icons-material/FormatBold";
import FormatItalicIcon from "@mui/icons-material/FormatItalic";
import FormatUnderlinedIcon from "@mui/icons-material/FormatUnderlined";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import FormatListNumberedIcon from "@mui/icons-material/FormatListNumbered";
import FormatAlignLeftIcon from "@mui/icons-material/FormatAlignLeft";
import FormatAlignCenterIcon from "@mui/icons-material/FormatAlignCenter";
import FormatAlignRightIcon from "@mui/icons-material/FormatAlignRight";
import FormatAlignJustifyIcon from "@mui/icons-material/FormatAlignJustify";
import UndoIcon from "@mui/icons-material/Undo";
import RedoIcon from "@mui/icons-material/Redo";
import {
  BLOCK_TYPES,
  FONT_SIZES,
  blocksFromHtmlNode,
  blocksToHtml,
  blocksToPlainText,
} from "./consentContent";

const ConsentContentEditor = ({
  blocks,
  disabled = false,
  onChange,
  placeholder = "Write the consent here — what the procedure is, its benefits, its risks, the alternatives and what the patient is agreeing to.",
}) => {
  const ref = useRef(null);
  const seeded = useRef(false);
  /** What the surface last read as — see `emit`. */
  const last = useRef("[]");

  // Seeded once per consent, then left to the browser. `blocks` changing from
  // our own `onChange` must not rewrite the DOM under the caret.
  useEffect(() => {
    const node = ref.current;
    if (!node || seeded.current) return;

    node.innerHTML = blocksToHtml(blocks) || "<p><br></p>";
    seeded.current = true;
    last.current = JSON.stringify(blocksFromHtmlNode(node));
  }, [blocks]);

  const emit = useCallback(() => {
    if (!ref.current) return;

    const next = blocksFromHtmlNode(ref.current);
    const serialized = JSON.stringify(next);
    if (serialized === last.current) return;

    last.current = serialized;
    onChange?.(next);
  }, [onChange]);

  const command = (name, value) => {
    if (disabled || !ref.current) return;

    ref.current.focus();
    try {
      document.execCommand("styleWithCSS", false, true);
      document.execCommand(name, false, value);
    } catch {
      // An unsupported command simply does nothing; the text is never lost.
    }
    emit();
  };

  const selectedBlocks = () => {
    const root = ref.current;
    const selection = window.getSelection?.();
    if (!root) return [];

    const inside = (node) =>
      node && (node === root ? null : root.contains(node) ? node : null);

    const blockOf = (node) => {
      let current = node?.nodeType === 3 ? node.parentNode : node;
      while (inside(current) && current.parentNode !== root) {
        current = current.parentNode;
      }
      return inside(current);
    };

    if (!selection || selection.rangeCount === 0) {
      return Array.from(root.children);
    }

    const range = selection.getRangeAt(0);
    const from = blockOf(range.startContainer);
    const to = blockOf(range.endContainer);
    const children = Array.from(root.children);

    if (!from && !to) return children;

    const start = Math.max(0, children.indexOf(from));
    const end = to ? children.indexOf(to) : start;

    return children.slice(start, Math.max(start, end) + 1);
  };

  /** Font size on every line the selection touches, lists included. */
  const applyFontSize = (size) => {
    if (disabled || !ref.current) return;

    for (const node of selectedBlocks()) {
      const targets =
        node.tagName === "UL" || node.tagName === "OL"
          ? Array.from(node.children)
          : [node];
      for (const target of targets) target.style.fontSize = `${size}pt`;
    }
    emit();
  };

  const applyBlockType = (tag) => {
    // `formatBlock` keeps the line's text and its marks and only changes the
    // tag, which is exactly what "make this a heading" means.
    command("formatBlock", `<${tag}>`);
  };

  const empty = blocksToPlainText(blocks).trim() === "";

  const toolbarButton = (title, icon, onClick, key) => (
    <Tooltip title={title} key={key || title}>
      <span>
        <IconButton
          size="small"
          disabled={disabled}
          aria-label={title}
          onMouseDown={(event) => event.preventDefault()}
          onClick={onClick}
        >
          {icon}
        </IconButton>
      </span>
    </Tooltip>
  );

  return (
    <Box className="consent-content">
      <Box className="consent-content__toolbar" role="toolbar" aria-label="Formatting">
        <TextField
          select
          size="small"
          label="Style"
          disabled={disabled}
          value=""
          sx={{ width: 140 }}
          SelectProps={{ displayEmpty: true, renderValue: () => "Style" }}
          inputProps={{ "aria-label": "Text style" }}
          onChange={(event) => applyBlockType(event.target.value)}
        >
          {BLOCK_TYPES.map((option) => (
            <MenuItem key={option.tag} value={option.tag}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        <TextField
          select
          size="small"
          label="Size"
          disabled={disabled}
          value=""
          sx={{ width: 104 }}
          SelectProps={{ displayEmpty: true, renderValue: () => "Size" }}
          inputProps={{ "aria-label": "Font size" }}
          onChange={(event) => applyFontSize(Number(event.target.value))}
        >
          {FONT_SIZES.map((size) => (
            <MenuItem key={size} value={size}>
              {size} pt
            </MenuItem>
          ))}
        </TextField>

        <Divider orientation="vertical" flexItem />

        {toolbarButton("Bold", <FormatBoldIcon fontSize="small" />, () => command("bold"))}
        {toolbarButton("Italic", <FormatItalicIcon fontSize="small" />, () =>
          command("italic")
        )}
        {toolbarButton("Underline", <FormatUnderlinedIcon fontSize="small" />, () =>
          command("underline")
        )}

        <Divider orientation="vertical" flexItem />

        {toolbarButton("Bulleted list", <FormatListBulletedIcon fontSize="small" />, () =>
          command("insertUnorderedList")
        )}
        {toolbarButton("Numbered list", <FormatListNumberedIcon fontSize="small" />, () =>
          command("insertOrderedList")
        )}

        <Divider orientation="vertical" flexItem />

        {toolbarButton("Align left", <FormatAlignLeftIcon fontSize="small" />, () =>
          command("justifyLeft")
        )}
        {toolbarButton("Align centre", <FormatAlignCenterIcon fontSize="small" />, () =>
          command("justifyCenter")
        )}
        {toolbarButton("Align right", <FormatAlignRightIcon fontSize="small" />, () =>
          command("justifyRight")
        )}
        {toolbarButton("Justify", <FormatAlignJustifyIcon fontSize="small" />, () =>
          command("justifyFull")
        )}

        <Divider orientation="vertical" flexItem />

        {toolbarButton("Undo", <UndoIcon fontSize="small" />, () => command("undo"))}
        {toolbarButton("Redo", <RedoIcon fontSize="small" />, () => command("redo"))}
      </Box>

      <div
        ref={ref}
        className="consent-content__surface"
        data-testid="consent-content-editor"
        data-empty={empty}
        data-placeholder={placeholder}
        role="textbox"
        aria-multiline="true"
        aria-label="Consent content"
        contentEditable={!disabled}
        suppressContentEditableWarning
        onInput={emit}
        onBlur={emit}
        onPaste={(event) => {
          if (disabled) return;
          event.preventDefault();
          const plain = event.clipboardData?.getData("text/plain") || "";
          document.execCommand("insertText", false, plain);
          emit();
        }}
      />
    </Box>
  );
};

export default ConsentContentEditor;
